/**
 * The Day - live daily content directly under the hero.
 *
 * Shows today's appointed readings and commemorated saints on both the
 * New (Revised Julian) and Old (Julian) calendars, fetched from the API
 * with an explicit `calendar_style` so anonymous visitors see both styles
 * regardless of any stored preference.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fixedDateFor, fromIso, type CalendarStyle } from '@orthodox-way/liturgical-engine';
import { apiFetch } from '@/lib/api.js';
import { useReveal } from '../hooks/useReveal.js';

type ReadingType = 'epistle' | 'gospel' | 'old_testament' | 'matins_gospel';

type ReadingsToday = {
  date: string;
  readings: Array<{ type: ReadingType; reference: string }>;
  title: string | null;
  commemorations: string | null;
};

type SaintsToday = {
  date: string;
  saints: Array<{ id: number; slug: string; name: string; title: string | null; type: string }>;
};

const READING_LABEL: Record<ReadingType, string> = {
  epistle: 'Epistle',
  gospel: 'Gospel',
  old_testament: 'Old Testament',
  matins_gospel: 'Matins Gospel',
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function civilDateLong(iso: string): string {
  const parts = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(parts[0] ?? 1970, (parts[1] ?? 1) - 1, parts[2] ?? 1));
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The menaion (fixed-cycle) date this calendar style reads today, e.g. "24 July". */
function menaionDate(iso: string, style: CalendarStyle): string {
  const fixed = fixedDateFor(fromIso(iso), style);
  return `${fixed.day} ${MONTHS[fixed.month - 1]}`;
}

/** Lectionary references carry an internal "Composite N -" prefix; hide it here. */
function cleanReference(reference: string): string {
  return reference.replace(/^Composite \d+\s*-\s*/i, '');
}

function useDayContent(style: CalendarStyle) {
  const readings = useQuery({
    queryKey: ['landing', 'readings-today', style],
    queryFn: ({ signal }) =>
      apiFetch<ReadingsToday>(`/readings/today?calendar_style=${style}`, {
        skipAuth: true,
        signal,
      }),
    staleTime: 5 * 60_000,
  });
  const saints = useQuery({
    queryKey: ['landing', 'saints-today', style],
    queryFn: ({ signal }) =>
      apiFetch<SaintsToday>(`/saints/today?calendar_style=${style}`, { skipAuth: true, signal }),
    staleTime: 5 * 60_000,
  });
  return { readings, saints };
}

export function TheDay() {
  const headRef = useReveal<HTMLDivElement>(0.3);
  const gridRef = useReveal<HTMLDivElement>(0.15);
  const iso = todayIso();

  return (
    <section id="this-day" className="relative w-full" style={{ padding: '76px 20px' }}>
      <div className="mx-auto max-w-container">
        <div ref={headRef} className="ow-reveal text-center">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            This Day
          </p>
          <h2
            className="font-display text-primary mt-4"
            style={{
              fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.15,
            }}
          >
            {civilDateLong(iso)}
          </h2>
          <p className="text-secondary mt-4 mx-auto" style={{ fontSize: 16, maxWidth: 560 }}>
            The readings and saints appointed for this day, on both calendars of the Church.
          </p>
        </div>

        <div ref={gridRef} className="ow-reveal mt-12 grid lg:grid-cols-2 gap-6 items-start">
          <CalendarColumn style="new" label="New Calendar" sub="Revised Julian" iso={iso} />
          <CalendarColumn style="old" label="Old Calendar" sub="Julian" iso={iso} />
        </div>
      </div>
    </section>
  );
}

function CalendarColumn({
  style,
  label,
  sub,
  iso,
}: {
  style: CalendarStyle;
  label: string;
  sub: string;
  iso: string;
}) {
  const { readings, saints } = useDayContent(style);
  const title = readings.data?.title ?? null;
  const readingList = readings.data?.readings ?? [];
  const saintList = saints.data?.saints ?? [];
  const loading = readings.isLoading || saints.isLoading;
  const failed = readings.isError && saints.isError;

  return (
    <article
      className="bg-bg-elevated border border-border rounded-card h-full"
      style={{ padding: 28, boxShadow: 'var(--ow-shadow-md)' }}
    >
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3
          className="font-display uppercase text-primary"
          style={{ fontSize: 14, fontWeight: 500, letterSpacing: '0.18em' }}
        >
          {label}
        </h3>
        <p
          className="font-display uppercase text-muted"
          style={{ fontSize: 10, letterSpacing: '0.24em' }}
        >
          {sub} · {menaionDate(iso, style)}
        </p>
      </header>

      {title && (
        <p className="scripture text-secondary mt-4" style={{ fontSize: 18, lineHeight: 1.5 }}>
          {title}
        </p>
      )}

      {failed && (
        <p className="text-muted mt-6" style={{ fontSize: 13 }}>
          The day&apos;s texts are unavailable at the moment.
        </p>
      )}

      {loading && !failed && (
        <div className="mt-6 space-y-3" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-sm bg-bg-subtle motion-safe:animate-pulse"
              style={{ height: 14, width: `${80 - i * 15}%` }}
            />
          ))}
        </div>
      )}

      {!loading && readingList.length > 0 && (
        <div className="mt-6 border-t border-border pt-5">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 9, letterSpacing: '0.3em' }}
          >
            Readings
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {readingList.slice(0, 6).map((r, i) => (
              <li key={`${r.type}-${r.reference}-${i}`}>
                <p
                  className="font-display uppercase text-muted"
                  style={{ fontSize: 9, letterSpacing: '0.3em' }}
                >
                  {READING_LABEL[r.type]}
                </p>
                <p className="text-secondary mt-0.5" style={{ fontSize: 14 }}>
                  {cleanReference(r.reference)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loading && saintList.length > 0 && (
        <div className="mt-6 border-t border-border pt-5">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 9, letterSpacing: '0.3em' }}
          >
            Saints of the Day
          </p>
          <ul className="mt-3 space-y-2">
            {saintList.slice(0, 5).map((s) => (
              <li key={s.id} style={{ fontSize: 14, lineHeight: 1.5 }}>
                <Link to={`/saints/${s.slug}`} className="text-primary hover:text-gold transition">
                  {s.name}
                </Link>
                {s.title && (
                  <span className="text-muted" style={{ fontSize: 13 }}>
                    , {s.title}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {saintList.length > 5 && (
            <p className="text-muted mt-2" style={{ fontSize: 12 }}>
              and {saintList.length - 5} more commemorated this day
            </p>
          )}
        </div>
      )}

      <footer className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
        <Link
          to="/readings"
          className="ow-underline font-display uppercase text-gold"
          style={{ fontSize: 10, letterSpacing: '0.22em' }}
        >
          Read the passages
        </Link>
        <Link
          to="/saints"
          className="ow-underline font-display uppercase text-gold"
          style={{ fontSize: 10, letterSpacing: '0.22em' }}
        >
          All saints of the day
        </Link>
      </footer>
    </article>
  );
}
